// Copy for the North Star prototype — a fresh, standalone take on the AI-first
// journey from the "path to Series A" PRD: land, talk to Keni, see what you can
// claim, then a stripped-back home built around a triage-driven task list.
// Copy only lives here; mock session data (found benefits, tasks, the scripted
// answers) lives in NorthStar.fixtures.ts.

// The person we're funding care for and the carer using the app — kept as
// constants so the scripted copy reads consistently across screens.
export const RECIPIENT = 'Mum';
export const CARER_FIRST_NAME = 'Olive';

export const LANDING = {
  greeting: "Hi, I'm Keni",
  // Stops at the offer. It used to end "for Mum — no forms": naming the recipient
  // on the first screen assumes an answer nobody has given yet, and "no forms"
  // was the app selling itself against the thing it is replacing, on a screen
  // where nobody has seen a form yet.
  blurb:
    "I help families fund care. Let's have a quick chat and I'll work out what money you could claim.",
  speak: 'Talk to Keni',
  type: 'Type instead',
};

export type QuickReply = { id: string; label: string };

export type TriageQuestion = {
  id: string;
  // What Keni says for this step.
  keni: string;
  // Fast-path chips. Picking one answers the question.
  replies: QuickReply[];
  // Placeholder for the free-text fallback.
  placeholder: string;
};

// A short, scripted triage — five questions, quick-reply first, text as a
// fallback. Deliberately conversational: Keni asks, it never feels like a form.
export const TRIAGE_QUESTIONS: TriageQuestion[] = [
  {
    id: 'who',
    keni: 'First up — who are we sorting care for?',
    replies: [
      { id: 'mum', label: 'My mum' },
      { id: 'dad', label: 'My dad' },
      { id: 'partner', label: 'My partner' },
      { id: 'other', label: 'Someone else' },
    ],
    placeholder: 'Tell me who…',
  },
  {
    id: 'where',
    keni: 'Where does Mum live at the moment?',
    replies: [
      { id: 'home', label: 'At home' },
      { id: 'with-me', label: 'With me' },
      { id: 'home-soon', label: 'Moving to a care home' },
    ],
    placeholder: 'Where does she live?',
  },
  {
    id: 'help',
    keni: 'Does Mum need help with everyday things — washing, dressing, meals?',
    replies: [
      { id: 'lots', label: 'Yes, a lot' },
      { id: 'some', label: 'Some help' },
      { id: 'not-yet', label: 'Not yet' },
    ],
    placeholder: 'What does she need help with?',
  },
  {
    id: 'cost',
    keni: 'Roughly, what is care costing each month right now?',
    replies: [
      { id: 'lt2', label: 'Under £2k' },
      { id: '2to4', label: '£2k–£4k' },
      { id: 'gt4', label: 'Over £4k' },
      { id: 'unsure', label: 'Not sure' },
    ],
    placeholder: 'A rough monthly figure…',
  },
  {
    id: 'pension',
    keni: 'Last one — is Mum over State Pension age (66)?',
    replies: [
      { id: 'yes', label: 'Yes' },
      { id: 'no', label: 'No' },
      { id: 'unsure', label: 'Not sure' },
    ],
    placeholder: 'Yes, no, or not sure',
  },
];

export const TRIAGE = {
  // Shown while Keni "works it out" between the last answer and the reveal.
  thinking: 'Thanks — give me a second to work out what Mum could claim…',
  // Voice-first states.
  speaking: 'Keni is speaking…',
  listening: 'Listening…',
  // Manual stepping, shown when autoplay is paused (for inspecting).
  next: 'Next →',
  continue: 'Continue →',
  ready: 'Ready when you are',
};

export const REVEAL = {
  heading: 'Here is what I found',
  claimLead: 'Based on what you’ve told me, Mum could claim',
  claimPerYear: 'a year',
  cta: 'Next',
};

export const HOME = {
  greeting: `Welcome, ${CARER_FIRST_NAME}`,
  subtitle: 'Here’s where things are with Mum’s care.',
  fundingTitle: 'You could claim',
  fundingPerYear: 'a year',
  fundingSub: '3 benefits found',
  tasksTitle: 'Tasks',
};

export const CARE = {
  title: 'Care',
  blurb: 'Search and compare care near Mum — coming soon.',
};

export const CLAIMS = {
  title: 'Claims',
  eligibleTitle: 'What you can claim',
  eligiblePerYear: 'a year',
  applicationsTitle: 'Your applications',
  applicationsSub: 'Where each claim is up to.',
};
